import React, { useRef, useState } from 'react';
import {
  IonModal,
  IonContent,
  IonButton,
  IonCheckbox,
  IonSpinner,
  IonInputOtp,
} from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneInput from '@/components/PhoneInput';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import { usePhoneVerification } from '@/modules/idv/usePhoneVerification';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { PhoneLogic } from '@/modules/phone';
import { MePrFoService } from '@/services/meprfo';
import { apiCall } from '@/util/api';
import {
  INTERSTITIAL_BREAKPOINT,
  INTERSTITIAL_BREAKPOINTS,
  pxToRem,
} from '@/modules/config';
import '@/components/Interstitial.css';

interface PhoneLinkingInterstitialProps {
  isOpen: boolean;
  onDismiss: () => void;
  /**
   * Open with "One more thing: ", politely, when another interstitial has
   * already interrupted the sender.
   */
  showOneMoreThingMsg?: boolean;
}

const PhoneLinkingInterstitial: React.FC<PhoneLinkingInterstitialProps> = ({
  isOpen,
  onDismiss,
  showOneMoreThingMsg = false,
}) => {
  const { suppress, unsuppress } = useSuppressibleNotices();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [validPhone, setValidPhone] =
    useState<ReturnType<typeof PhoneLogic.tryCreate>>(null);
  const [linking, setLinking] = useState(false);
  const [presentToast] = useAppToast();

  const {
    claims,
    phone: contextPhone,
    setPhone: setContextPhone,
    refreshUserContext,
  } = useUserContext();

  const handleDismiss = () => {
    onDismiss();
  };

  const {
    status,
    code,
    setCode,
    sendSms,
    resend,
    reset: resetVerification,
  } = usePhoneVerification({
    phone: validPhone,
    continuePath: window.location.pathname,
    onSuccess: async () => {
      await presentToast({
        message: 'Phone registered! Your introductions will now be counted.',
        duration: 3000,
        color: 'success',
      });
      handleDismiss();
    },
    onExpired: async () => {
      await presentToast({
        message: 'Verification code expired. Please try again.',
        duration: 3000,
        color: 'warning',
      });
      resetVerification();
    },
    onInvalidCode: async message => {
      await presentToast({
        message,
        duration: 3000,
        color: 'danger',
      });
    },
  });

  const expandToAnchor = async (anchorId: string) => {
    await modalRef.current?.setCurrentBreakpoint(1);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        contentRef.current?.scrollToPoint(0, el.offsetTop - 16, 300);
      }
    }, 300);
  };

  const handleCheckboxChange = (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked) suppress('phoneLinking');
    else unsuppress('phoneLinking');
  };

  const handleVerifyWithSms = async () => {
    if (!validPhone) return;
    setLinking(true);
    try {
      // Refresh auth state first so we never act on a stale or expired session.
      const freshUser = await refreshUserContext();

      const {
        userId: existingUserId,
        phoneOprf,
        phoneTail,
      } = await MePrFoService.checkPhoneExists(validPhone.value);

      if (existingUserId) {
        if (freshUser?.id && freshUser.id === existingUserId) {
          // Phone already belongs to this user — proceed to SMS verification.
          setContextPhone(validPhone);
          await sendSms();
          return;
        }
        if (!freshUser) {
          const identifyResponse = await apiCall('/api/auth/identify', {
            method: 'POST',
            body: JSON.stringify({ id: existingUserId }),
          });
          if (!identifyResponse.ok) {
            throw new Error('Failed to link to existing account');
          }
          // Best-effort — identify already established the session cookie.
          // If this refresh returns null (transient DB hiccup), sendSms will
          // still authenticate correctly because the cookie is valid.
          await refreshUserContext();
        }
        setContextPhone(validPhone);
        await sendSms();
        return;
      }

      // Create new user with phone, reusing the OPRF + tail from the lookup.
      const createResponse = await apiCall('/api/users', {
        method: 'PUT',
        body: JSON.stringify({ phoneTail, phoneOprf }),
      });
      if (!createResponse.ok) {
        const text = await createResponse.text();
        throw new Error(text || 'Failed to register phone');
      }

      const { id: newUserId } = await createResponse.json();

      const identifyResponse = await apiCall('/api/auth/identify', {
        method: 'POST',
        body: JSON.stringify({ id: newUserId }),
      });
      if (!identifyResponse.ok) {
        throw new Error('Failed to authenticate after registration');
      }

      await refreshUserContext();
      setContextPhone(validPhone);
      await sendSms();
    } catch (error) {
      await presentToast({
        message: error instanceof Error ? error.message : 'Registration failed',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setLinking(false);
    }
  };

  const isAlreadyVerified = ClaimsLogic.isPhoneVerified(claims);
  const isCodeEntry = status === 'awaiting-code' || status === 'verifying';
  const isPreparing = linking || status === 'sending';
  const verifyingDigits = status === 'verifying';
  const skipLabel = isCodeEntry ? 'Skip phone verification' : 'Skip for now';

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={INTERSTITIAL_BREAKPOINT}
      breakpoints={INTERSTITIAL_BREAKPOINTS}
      handle
      onDidDismiss={handleDismiss}
    >
      <IonContent ref={contentRef} className="ion-padding-top">
        {isAlreadyVerified ? (
          <>
            <p className="interstitial__message">
              Your phone is already linked with Snowball. Your introductions are
              being counted.
            </p>
            <IonButton
              expand="block"
              className="interstitial__continue-btn"
              onClick={handleDismiss}
            >
              Continue
            </IonButton>
          </>
        ) : (
          <>
            <p className="interstitial__message">
              {showOneMoreThingMsg && 'One more thing: '}Want to track how
              you've accelerated awareness about this topic?{' '}
              <button
                className="interstitial__link"
                onClick={() => expandToAnchor('phone-entry')}
              >
                Link your phone number
              </button>{' '}
              with Snowball below so we can collect proper stats{' '}
              <span style={{ whiteSpace: 'nowrap' }}>
                (
                <button
                  className="interstitial__link"
                  onClick={() => expandToAnchor('phone-convince')}
                >
                  convince me
                </button>
                )
              </span>
              .
            </p>

            <div className="interstitial__checkbox-row">
              <IonCheckbox
                checked={dontShowAgain}
                onIonChange={e => handleCheckboxChange(e.detail.checked)}
              />
              <span
                style={{
                  fontSize: pxToRem(14),
                  color: 'var(--ion-color-medium)',
                }}
              >
                Got it!
              </span>
            </div>

            <IonButton
              expand="block"
              className="interstitial__continue-btn"
              fill="outline"
              onClick={handleDismiss}
            >
              {skipLabel}
            </IonButton>

            {/* Detail zone */}
            <hr className="interstitial__section-rule" />

            <div id="phone-entry">
              <p className="interstitial__section-heading">
                Link your phone number
              </p>

              {!isCodeEntry && (
                <>
                  <div style={{ padding: '0 16px 12px' }}>
                    <PhoneInput
                      value={phoneInput}
                      onPhoneChange={setPhoneInput}
                      onValidityChange={phone => setValidPhone(phone)}
                      placeholder="(555) 123-4567"
                      showValidation
                    />
                  </div>
                  <div style={{ padding: '0 16px 4px' }}>
                    <IonButton
                      expand="block"
                      disabled={!validPhone || isPreparing}
                      onClick={handleVerifyWithSms}
                    >
                      {isPreparing ? (
                        <IonSpinner name="dots" />
                      ) : (
                        'Verify with SMS'
                      )}
                    </IonButton>
                  </div>
                  <p className="interstitial__sms-consent">
                    By providing your number, you agree to receive a one-time
                    SMS from Snowball. Message/data rates apply.
                  </p>
                </>
              )}

              {isCodeEntry && (
                <>
                  <p className="interstitial__phone-readonly">
                    {validPhone
                      ? PhoneLogic.format(validPhone.value)
                      : contextPhone
                        ? PhoneLogic.format(contextPhone.value)
                        : ''}
                  </p>
                  <div className="interstitial__otp">
                    <IonInputOtp
                      length={6}
                      separators={[3]}
                      value={code}
                      disabled={verifyingDigits}
                      onIonInput={e => {
                        const value = e.detail.value || '';
                        const digitsOnly = value
                          .toString()
                          .replace(/\D/g, '')
                          .slice(0, 6);
                        setCode(digitsOnly);
                      }}
                    />
                  </div>
                  <div style={{ padding: '0 16px 16px' }}>
                    <IonButton
                      expand="block"
                      fill="outline"
                      disabled={verifyingDigits}
                      onClick={() => resend()}
                    >
                      Resend SMS
                    </IonButton>
                  </div>
                </>
              )}
            </div>

            {!isCodeEntry && (
              <>
                <hr className="interstitial__section-rule" />

                <div id="phone-convince">
                  <p className="interstitial__section-heading">Why link?</p>
                  <p className="interstitial__body">
                    Stats are associated with an encrypted version of your phone
                    number that only your device can decode. At most we may need
                    to temporarily relay the number you provide to Twilio, an
                    SMS gateway trusted by thousands of apps, for identity
                    verification purposes.
                  </p>
                </div>
              </>
            )}

            <div style={{ height: 40 }} />
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default PhoneLinkingInterstitial;
